import { Header } from './Header';
import { AdventStrip } from '../season/AdventStrip';
import { FamilyCalendar } from '../calendar/FamilyCalendar';
import { WidgetBoard } from '../widgets/WidgetBoard';
import { MobileStack } from '../widgets/MobileStack';
import { DayPanel } from '../calendar/DayPanel';
import { EventEditor } from '../calendar/EventEditor';
import { AlertOverlay } from '../alerts/AlertOverlay';
import { ComposeAlert } from '../alerts/ComposeAlert';
import { useAppStore } from '../../stores/appStore';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

function ArtCredit() {
  return (
    <p className="shrink-0 px-3 pb-1 text-center text-[10px] leading-tight text-slate-500">
      Emoji animation by Google Noto, CC BY 4.0. Fluent Emoji by Microsoft, MIT.
    </p>
  );
}

/**
 * Main dashboard:
 * - Desktop/TV: calendar top ~55%, widgets grid bottom
 * - Mobile: full-page scroll — calendar first, then stacked widgets
 */
export function Dashboard() {
  const kiosk = useAppStore((s) => s.kiosk);
  const dayPanelOpen = useAppStore((s) => s.dayPanelOpen);
  const eventEditorOpen = useAppStore((s) => s.eventEditor.open);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mobile: page scrolls as one column
  if (isMobile && !kiosk) {
    return (
      <div
        className={clsx(
          'flex min-h-full flex-col bg-slate-950 text-slate-100',
          'light:bg-slate-50 light:text-slate-900'
        )}
      >
        <Header />
        <main className="flex flex-1 flex-col gap-3 overflow-y-auto p-3 pb-10">
          <section
            data-calendar-stage
            className="flex h-[min(70vh,520px)] min-h-[320px] shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/70 shadow-xl light:border-slate-200 light:bg-white"
          >
            <AdventStrip />
            <div className="min-h-0 flex-1">
              <FamilyCalendar />
            </div>
          </section>
          <MobileStack />
        </main>
        <ArtCredit />
        {dayPanelOpen && <DayPanel />}
        {eventEditorOpen && <EventEditor />}
        <ComposeAlert />
        <AlertOverlay />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex h-full min-h-0 flex-col bg-slate-950 text-slate-100',
        'light:bg-slate-50 light:text-slate-900',
        kiosk && 'kiosk-mode'
      )}
    >
      <Header />

      <main className="flex min-h-0 flex-1 flex-col gap-3 p-3 md:p-4 lg:gap-4 lg:p-5">
        <section
          data-calendar-stage
          className={clsx(
            'flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/70 shadow-xl',
            'light:border-slate-200 light:bg-white',
            'h-[55vh] min-h-[280px] flex-[1.35] md:h-auto'
          )}
        >
          <AdventStrip />
          <div className="min-h-0 flex-1">
            <FamilyCalendar />
          </div>
        </section>

        <section
          className={clsx(
            'min-h-0 flex-1 overflow-auto rounded-2xl border border-slate-700/40 bg-slate-900/40 p-2 md:p-3',
            'light:border-slate-200 light:bg-slate-100/80'
          )}
        >
          <WidgetBoard />
        </section>
      </main>
      <ArtCredit />

      {dayPanelOpen && <DayPanel />}
      {eventEditorOpen && <EventEditor />}
      <ComposeAlert />
      <AlertOverlay />
    </div>
  );
}
